/**
 * Scale Pad Component
 * Realistic representation of a single scale pad with LED glow effect
 */

import { useMScaleStore } from '../../store'
import { formatWeight } from '../../utils/format'

interface ScalePadProps {
  position: number
  weight: number
  pairId: number | null
  isActive: boolean
  onClick?: () => void
}

const PAIR_COLORS = {
  0: { rgb: '0, 255, 0', name: 'Green' },      // Green
  1: { rgb: '0, 100, 255', name: 'Blue' },     // Blue
  2: { rgb: '255, 220, 0', name: 'Yellow' },   // Yellow
  3: { rgb: '255, 0, 0', name: 'Red' },        // Red
}

export function ScalePad({ weight, pairId, isActive, onClick }: ScalePadProps) {
  const { showHundredthsUnder10g } = useMScaleStore()
  const hasPair = pairId !== null && pairId >= 0 && pairId <= 3
  const pairColor = hasPair ? PAIR_COLORS[pairId as keyof typeof PAIR_COLORS] : null

  return (
    <button
      onClick={onClick}
      disabled={!isActive}
      className={`
        relative aspect-square
        transition-all duration-300
        ${isActive ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}
        group
      `}
      style={{
        perspective: '1000px',
      }}
    >
      {/* Pad Container with 3D perspective */}
      <div
        className="relative w-full h-full"
        style={{
          transform: 'rotateX(2deg)',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Pad Base (dark gray border/frame) */}
        <div
          className="absolute inset-0 rounded-md"
          style={{
            backgroundColor: '#4a5568',
            transform: 'translateZ(-4px)',
          }}
        />

        {/* Main Pad Surface (translucent white) */}
        <div
          className={`
            absolute inset-0.5 rounded-sm
            transition-all duration-500
            ${hasPair ? 'shadow-2xl' : 'shadow-lg'}
          `}
          style={{
            backgroundColor: isActive ? 'rgba(255, 255, 255, 0.95)' : 'rgba(200, 200, 200, 0.5)',
            backdropFilter: 'blur(10px)',
            transform: 'translateZ(0px)',
            boxShadow: hasPair && pairColor
              ? `
                0 0 30px rgba(${pairColor.rgb}, 0.6),
                0 0 60px rgba(${pairColor.rgb}, 0.4),
                inset 0 -20px 40px rgba(${pairColor.rgb}, 0.3)
              `
              : isActive
                ? '0 4px 6px rgba(0, 0, 0, 0.1)'
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* LED Glow Effect (bottom-left corner) */}
          {hasPair && pairColor && (
            <div
              className="absolute bottom-0 left-0 w-1/2 h-1/2 rounded-tr-full opacity-80 animate-pulse"
              style={{
                background: `radial-gradient(circle at bottom left, rgba(${pairColor.rgb}, 0.8), rgba(${pairColor.rgb}, 0.3) 60%, transparent 80%)`,
                filter: 'blur(8px)',
              }}
            />
          )}

          {/* Weight Display */}
          {isActive && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-1 sm:p-2">
              <div
                className={`
                  text-sm sm:text-lg md:text-xl lg:text-2xl font-mono font-bold leading-tight
                  transition-all duration-300
                  ${hasPair ? 'text-gray-900' : 'text-gray-700'}
                `}
                style={{
                  textShadow: hasPair && pairColor
                    ? `0 0 10px rgba(${pairColor.rgb}, 0.3)`
                    : 'none',
                }}
              >
                {formatWeight(weight, showHundredthsUnder10g)}
              </div>
              <div className="text-[8px] sm:text-[10px] md:text-xs text-gray-500 font-medium">
                grams
              </div>
            </div>
          )}

          {/* Inactive State */}
          {!isActive && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-base sm:text-lg text-gray-400">—</div>
            </div>
          )}
        </div>

        {/* Hover Effect for Active Pads */}
        {isActive && (
          <div
            className="absolute inset-0.5 rounded-sm pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%)',
              transform: 'translateZ(1px)',
            }}
          />
        )}
      </div>
    </button>
  )
}
